import React, { useEffect, useState } from 'react';
import Navigation from '@/components/Navigation';
import PageExplainer from '@/components/PageExplainer';
import { Card } from '@/components/ui/card';
import {
  Users, Calendar, BookOpen, MessageSquare, FileText, FlaskConical,
  Building2, Star, ChevronRight, Zap, Target, Send, CheckCircle, Clock, Mail, Inbox,
  Settings, Eye, EyeOff, ChevronDown, ChevronUp, Handshake, Shield,
  TrendingUp, Award, Sparkles, ArrowRight, UserCircle, Newspaper
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import DashboardRailWidgets from '@/components/dashboard/DashboardRailWidgets';
import WelcomeNote from '@/components/WelcomeNote';
import MemberAvatar from '@/components/MemberAvatar';
import RecommendedForYou from '@/components/RecommendedForYou';

const BACKEND_URL = '';

const WIDGET_DEFAULTS = {
  myActivity: true,
  messages: false,
  stats: true,
  charts: false,
  britelabAnalytics: false,
  recentActivity: true,
  upcomingEvents: true,
  newMembersPartners: true,
  latestNews: true,
  recommendations: true,
  quickActions: true,
};

const WIDGET_LABELS = {
  myActivity: 'Your Activity',
  messages: 'My Messages',
  stats: 'Stats Overview',
  charts: 'Charts & Analytics',
  britelabAnalytics: 'SLL BriteHub Analytics',
  recentActivity: 'Recent Activity',
  upcomingEvents: 'Upcoming Events',
  newMembersPartners: 'New Members & Partners',
  latestNews: 'Latest News',
  recommendations: 'Recommended Companies',
  quickActions: 'Quick Actions',
};

const WIDGET_PREFS_VERSION = 3;

function loadWidgetPrefs() {
  try {
    const ver = parseInt(localStorage.getItem('dash_widgets_v') || '1', 10);
    const saved = localStorage.getItem('dash_widgets');
    // Bumping the version drops stale prefs so newly-hidden widgets (e.g. charts)
    // stay hidden even for users whose old prefs had them toggled on.
    if (ver < WIDGET_PREFS_VERSION) {
      localStorage.setItem('dash_widgets_v', String(WIDGET_PREFS_VERSION));
      return { ...WIDGET_DEFAULTS };
    }
    return saved ? { ...WIDGET_DEFAULTS, ...JSON.parse(saved) } : { ...WIDGET_DEFAULTS };
  } catch { return { ...WIDGET_DEFAULTS }; }
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

// Fallback: derive a logo from a partner's website when no logo_url is stored.
// Uses Google's free favicon service (no auth, returns a transparent PNG).
function getPartnerLogoSrc(partner) {
  if (partner.logo_url) return partner.logo_url;
  const site = partner.website || '';
  if (!site) return '';
  try {
    const domain = site.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : '';
  } catch { return ''; }
}

export default function Dashboard({ user }) {
  const [stats, setStats] = useState({ events: 0, courses: 0, members: 0, research: 0, labs: 0, forum: 0 });
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [ChartsComponent, setChartsComponent] = useState(null);
  const [chartData, setChartData] = useState({ activity: [], topics: [], britelabs: [] });
  const [britelabStats, setBritelabStats] = useState(null);
  const [messageStats, setMessageStats] = useState(null);
  const [myActivity, setMyActivity] = useState(null);
  const [newMembers, setNewMembers] = useState([]);
  const [newPartners, setNewPartners] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [widgets, setWidgets] = useState(loadWidgetPrefs);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();

  const toggleWidget = (key) => {
    setWidgets(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('dash_widgets', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    import('@/components/DashboardCharts').then(mod => setChartsComponent(() => mod.DashboardCharts));
  }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/events`, { credentials: 'include' })
      .then(r => r.json()).then(data => {
        const upcoming = data.filter(e => new Date(e.date) >= new Date()).sort((a, b) => new Date(a.date) - new Date(b.date));
        setUpcomingEvents(upcoming.slice(0, 4));
        setStats(p => ({ ...p, events: upcoming.length }));
      }).catch(() => {});

    fetch(`${BACKEND_URL}/api/courses`, { credentials: 'include' })
      .then(r => r.json()).then(data => setStats(p => ({ ...p, courses: data.length }))).catch(() => {});

    fetch(`${BACKEND_URL}/api/members`, { credentials: 'include' })
      .then(r => r.json()).then(data => {
        setStats(p => ({ ...p, members: data.length }));
        // Newest 6 members (sorted desc by created_at) for the New Members
        // widget. Exclude the viewer themselves — showing yourself as a
        // "new member" right after you finish setting up your account
        // looks broken and doesn't tell you anything useful.
        const sorted = [...data]
          .filter(u => u.created_at && u.user_id !== user?.user_id)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setNewMembers(sorted.slice(0, 6));
        const now = new Date();
        const startDate = new Date(2026, 2, 1);
        const months = {};
        const d = new Date(startDate);
        while (d <= now) {
          months[d.toLocaleString('en', { month: 'short', year: '2-digit' })] = 0;
          d.setMonth(d.getMonth() + 1);
        }
        const stakeholders = {};
        const regions = {};
        data.forEach(u => {
          if (u.created_at) {
            const key = new Date(u.created_at).toLocaleString('en', { month: 'short', year: '2-digit' });
            if (months[key] !== undefined) months[key]++;
          }
          // Use `background` (the populated profile field). `stakeholder_category`
          // was a phantom field that left every member as "Not set".
          const sc = u.profile?.background || u.stakeholder_category || u.profile?.stakeholder_category || 'Not set';
          stakeholders[sc] = (stakeholders[sc] || 0) + 1;
          const region = u.country || u.profile?.country || u.location || u.profile?.location || 'Not set';
          regions[region] = (regions[region] || 0) + 1;
        });
        setChartData(prev => ({
          ...prev,
          activity: Object.entries(months).map(([month, count]) => ({ month, members: count })),
          stakeholderMix: Object.entries(stakeholders).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value })),
          regionMix: Object.entries(regions).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value })),
        }));
      }).catch(() => {});

    fetch(`${BACKEND_URL}/api/learning?limit=1`, { credentials: 'include' })
      .then(r => r.json()).then(data => setStats(p => ({ ...p, research: data.total || 0 }))).catch(() => {});

    fetch(`${BACKEND_URL}/api/britelabs`, { credentials: 'include' })
      .then(r => r.json()).then(data => {
        if (Array.isArray(data)) {
          // Status breakdown is per-user (only labs they're part of) — fine
          // for the status chart. The top metric "BriteLabs" should reflect
          // the total platform count, not just the user's labs — that comes
          // from /api/britelabs/dashboard below.
          const statusMap = {};
          data.forEach(lab => {
            const s = lab.status || 'open';
            statusMap[s] = (statusMap[s] || 0) + 1;
          });
          setChartData(prev => ({
            ...prev,
            britelabs: Object.entries(statusMap).map(([name, value]) => ({
              name: name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' '), value,
            })),
          }));
        }
      }).catch(() => {});

    // Accurate topic stats from dedicated endpoint
    fetch(`${BACKEND_URL}/api/forum/topic-stats`, { credentials: 'include' })
      .then(r => r.json()).then(data => {
        setChartData(prev => ({ ...prev, topics: data }));
      }).catch(() => {});

    fetch(`${BACKEND_URL}/api/forum/feed?limit=5`, { credentials: 'include' })
      .then(r => r.json()).then(data => {
        const posts = data.posts || [];
        setRecentPosts(posts.slice(0, 5));
        setStats(p => ({ ...p, forum: data.total || posts.length }));
      }).catch(() => {});

    fetch(`${BACKEND_URL}/api/recommendations/my`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { recommendations: [] })
      .then(data => setRecommendations(data.recommendations || []))
      .catch(() => {});

    fetch(`${BACKEND_URL}/api/britelabs/dashboard`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.overview) {
          setBritelabStats(data.overview);
          // Use the platform-wide total for the top "BriteLabs" metric tile.
          setStats(p => ({ ...p, labs: data.overview.total || 0 }));
        }
      })
      .catch(() => {});

    fetch(`${BACKEND_URL}/api/messages/stats`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setMessageStats(data); })
      .catch(() => {});

    fetch(`${BACKEND_URL}/api/dashboard/my-activity`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setMyActivity(data); })
      .catch(() => {});

    // Newest partners — sorted by created_at desc, limited to 6 for the widget.
    fetch(`${BACKEND_URL}/api/partners?limit=100`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const list = data?.partners || [];
        const sorted = [...list].filter(p => p.created_at).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setNewPartners(sorted.slice(0, 6));
      })
      .catch(() => {});

    // Latest news — top 4 items for the dashboard widget. Backend returns
    // newest-first already; we just trim and keep image/source/published.
    fetch(`${BACKEND_URL}/api/news`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const list = Array.isArray(data) ? data : (data?.news || []);
        setLatestNews(list.slice(0, 4));
      })
      .catch(() => {});
  }, [user?.user_id]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-20 md:pb-16 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="progress" type="top" className="mb-4 md:mb-6 flex justify-center" />

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">

          {/* Header */}
          <div className="flex items-start justify-between mb-5 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading mb-0.5 flex items-center gap-2">
                Welcome back, <span className="gradient-text">{user?.name?.split(' ')[0]}</span>
                <PageExplainer pageKey="dashboard" label="About the Dashboard" />
              </h1>
              <p className="text-slate-500 text-xs md:text-sm">Your SLL Community progress at a glance</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(!showSettings)}
              data-testid="toggle-widget-settings" className="text-slate-400 hover:text-slate-600">
              <Settings className="h-4 w-4" />
            </Button>
          </div>

          {/* Widget Settings Panel */}
          {showSettings && (
            <Card className="p-3 md:p-4 mb-4 border-slate-200 bg-white" data-testid="widget-settings-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Show / Hide Widgets</p>
                <button onClick={() => setShowSettings(false)} className="text-slate-400 hover:text-slate-600">
                  <ChevronUp className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {Object.entries(WIDGET_LABELS).map(([key, label]) => (
                  <button key={key} data-testid={`toggle-${key}`} onClick={() => toggleWidget(key)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      widgets[key] ? 'bg-brand-navy/10 text-brand-navy' : 'bg-slate-50 text-slate-400'
                    }`}>
                    {widgets[key] ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    {label}
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* Welcome note from the host */}
          <WelcomeNote user={user} />

          {/* Stats Overview — sits directly under Kelly's note for at-a-glance progress */}
          {widgets.stats && (
            <div className="grid grid-cols-3 xl:grid-cols-6 gap-2 md:gap-3 mb-4 md:mb-6 mt-4 md:mt-6" data-testid="stats-grid">
              {[
                { to: '/members', label: 'Members', value: stats.members, icon: Users, color: 'brand-navy', bg: 'bg-brand-navy/10' },
                { to: '/events', label: 'Events', value: stats.events, icon: Calendar, color: 'brand-sage', bg: 'bg-brand-sage/10' },
                { to: '/courses', label: 'Courses', value: stats.courses, icon: BookOpen, color: 'blue-600', bg: 'bg-blue-600/10' },
                { to: '/learning', label: 'Learning', value: stats.research, icon: FileText, color: 'amber-600', bg: 'bg-amber-600/10' },
                { to: '/britelabs', label: 'BriteLabs', value: stats.labs, icon: FlaskConical, color: 'purple-600', bg: 'bg-purple-600/10' },
                { to: '/forum', label: 'Discussions', value: stats.forum, icon: MessageSquare, color: 'emerald-600', bg: 'bg-emerald-600/10' },
              ].map(s => (
                <Link key={s.to} to={s.to}>
                  <Card className="relative overflow-hidden p-2.5 md:p-5 border-0 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group" data-testid={`stat-card-${s.label.toLowerCase()}`}>
                    <div className={`absolute inset-0 bg-${s.color} opacity-[0.04] group-hover:opacity-[0.08] transition-opacity`} />
                    <div className={`absolute -right-3 -bottom-3 text-${s.color} opacity-[0.06] hidden md:block`}><s.icon className="h-24 w-24" strokeWidth={1} /></div>
                    <div className="relative z-10">
                      <div className={`inline-flex items-center justify-center w-6 h-6 md:w-9 md:h-9 rounded-lg ${s.bg} mb-1 md:mb-3`}>
                        <s.icon className={`h-3 w-3 md:h-4 md:w-4 text-${s.color}`} />
                      </div>
                      <p className="text-[9px] md:text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">{s.label}</p>
                      <p className="text-lg md:text-3xl font-bold font-heading text-slate-800">{s.value}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* Your Activity Widget */}
          {widgets.myActivity && myActivity && (
            <div className="mb-4 md:mb-6" data-testid="my-activity-widget">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-brand-navy" />
                  <h3 className="text-xs md:text-sm font-bold text-slate-700">Your Activity</h3>
                  <span className="text-[10px] text-slate-400">Last 30 days</span>
                </div>
              </div>

              {/* Activity summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-3">
                {[
                  { label: 'Events Attended', value: myActivity.events_attended?.length || 0, icon: Calendar, color: 'text-orange-600', bg: 'bg-orange-50', to: '/events' },
                  { label: 'Courses In Progress', value: myActivity.courses?.length || 0, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50', to: '/courses' },
                  { label: 'Forum Posts', value: myActivity.forum_posts || 0, icon: MessageSquare, color: 'text-brand-navy', bg: 'bg-brand-navy/5', to: user?.user_id ? `/forum?user=${user.user_id}` : '/forum' },
                  { label: 'BriteLab Posts', value: myActivity.britelab_posts || 0, icon: FlaskConical, color: 'text-purple-600', bg: 'bg-purple-50', to: '/britelabs' },
                ].map(s => (
                  <Link key={s.label} to={s.to} data-testid={`activity-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <Card className="p-3 md:p-4 border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
                      <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg ${s.bg} flex items-center justify-center mb-1.5`}>
                        <s.icon className={`h-3.5 w-3.5 md:h-4 md:w-4 ${s.color}`} />
                      </div>
                      <p className="text-lg md:text-2xl font-bold text-slate-800">{s.value}</p>
                      <p className="text-[9px] md:text-[11px] text-slate-500 font-medium">{s.label}</p>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Events & courses detail */}
              <div className="grid md:grid-cols-2 gap-3">
                {/* Registered Events — ALL events the user has RSVP'd to (past + upcoming) */}
                {myActivity.events_registered?.length > 0 && (
                  <Card className="p-4 border-0 shadow-sm" data-testid="activity-events-detail">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-orange-500" /> Registered Events
                    </h4>
                    <div className="space-y-2">
                      {myActivity.events_registered.map(ev => {
                        const isUpcoming = ev.status === 'upcoming';
                        return (
                          <Link key={ev.event_id} to="/events" className="block" data-testid={`activity-event-${ev.event_id}`}>
                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                              <div className="w-9 text-center flex-shrink-0">
                                <div className={`text-[9px] font-bold uppercase ${isUpcoming ? 'text-brand-sage' : 'text-orange-500'}`}>
                                  {new Date(ev.date).toLocaleString('en', { month: 'short' })}
                                </div>
                                <div className="text-sm font-bold text-slate-800">{new Date(ev.date).getDate()}</div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-700 truncate">{ev.title}</p>
                                <p className="text-[10px] text-slate-400">
                                  {isUpcoming ? 'Upcoming' : 'Attended'}{ev.format ? ` · ${ev.format}` : ''}{ev.location ? ` · ${ev.location}` : ''}
                                </p>
                              </div>
                              {isUpcoming
                                ? <Clock className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                : <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* Completed Courses — only fully completed (progress >= 100) */}
                {myActivity.courses_completed?.length > 0 && (
                  <Card className="p-4 border-0 shadow-sm" data-testid="activity-courses-detail">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <BookOpen className="h-3.5 w-3.5 text-blue-500" /> Completed Courses
                    </h4>
                    <div className="space-y-3">
                      {myActivity.courses_completed.map(c => (
                        <Link key={c.course_id} to={`/courses/${c.course_id}`} className="block" data-testid={`activity-course-${c.course_id}`}>
                          <div className="p-2 rounded-lg hover:bg-slate-50 transition-colors">
                            <div className="flex items-center justify-between mb-1.5">
                              <p className="text-xs font-medium text-slate-700 truncate flex-1">{c.title}</p>
                              <span className="text-[10px] font-bold text-green-600 ml-2 flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" /> Completed
                              </span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-brand-sage" style={{ width: '100%' }} />
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">{c.completed_modules}/{c.total_modules} modules</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </Card>
                )}
              </div>

              <RecommendedForYou items={myActivity.recommendations?.items} />
            </div>
          )}

          {/* Messages Widget */}
          {widgets.messages && (
            <div className="mb-4 md:mb-6" data-testid="messages-widget">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-xs md:text-sm font-bold text-slate-700">My Messages</h3>
                </div>
                <Link to="/messages"><Button variant="ghost" size="sm" className="text-xs text-brand-navy h-7">View all <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button></Link>
              </div>
              <div className="grid grid-cols-3 gap-2 md:gap-4">
                {[
                  { key: 'total_messages', label: 'Total', icon: Mail, bg: 'bg-indigo-50', color: 'text-indigo-600', textColor: 'text-slate-800' },
                  { key: 'conversations', label: 'Conversations', icon: Inbox, bg: 'bg-sky-50', color: 'text-sky-600', textColor: 'text-slate-800' },
                  { key: 'unread', label: 'Unread', icon: Send, bg: 'bg-rose-50', color: 'text-rose-600', textColor: 'text-rose-600' },
                ].map(s => (
                  <Link key={s.key} to="/messages" data-testid={`msg-stat-${s.key}`}>
                    <Card className="p-2.5 md:p-5 border-0 shadow-sm text-center hover:shadow-md transition-shadow cursor-pointer">
                      <div className={`w-7 h-7 md:w-10 md:h-10 rounded-xl ${s.bg} flex items-center justify-center mx-auto mb-1 md:mb-2.5`}>
                        <s.icon className={`h-3.5 w-3.5 md:h-5 md:w-5 ${s.color}`} />
                      </div>
                      <p className={`text-lg md:text-3xl font-bold ${s.textColor}`}>{messageStats?.[s.key] || 0}</p>
                      <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 font-medium">{s.label}</p>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Stat Cards */}
          {/* Charts */}
          {widgets.charts && ChartsComponent && <ChartsComponent stats={{
            memberGrowth: chartData.activity.map(d => ({ month: d.month, count: d.members })),
            forumTopics: chartData.topics,
            labStatuses: chartData.britelabs,
            stakeholderMix: chartData.stakeholderMix || [],
            regionMix: chartData.regionMix || [],
          }} />}

          {/* BriteLab Analytics */}
          {widgets.britelabAnalytics && britelabStats && (
            <div className="mb-4 md:mb-6" data-testid="britelab-analytics-section">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="flex items-center gap-2">
                  <FlaskConical className="h-4 w-4 text-purple-600" />
                  <h3 className="text-xs md:text-sm font-bold text-slate-700">SLL BriteHub Analytics</h3>
                </div>
                <Link to="/britelabs"><Button variant="ghost" size="sm" className="text-xs text-brand-navy h-7">View all <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button></Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                {[
                  { key: 'total', label: 'Total Labs', icon: FlaskConical, bg: 'bg-brand-navy/10', color: 'text-brand-navy', textColor: 'text-slate-800' },
                  { key: 'active', label: 'Active', icon: Zap, bg: 'bg-brand-sage/10', color: 'text-brand-sage', textColor: 'text-brand-sage' },
                  { key: 'solved', label: 'Solved', icon: CheckCircle, bg: 'bg-brand-blue/10', color: 'text-brand-navy', textColor: 'text-brand-navy' },
                  { key: 'pending', label: 'Pending', icon: Clock, bg: 'bg-amber-50', color: 'text-amber-600', textColor: 'text-amber-700' },
                ].map(s => (
                  <Card key={s.key} className="p-3 md:p-5 border-0 shadow-sm text-center hover:shadow-md transition-shadow" data-testid={`bl-stat-${s.key}`}>
                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl ${s.bg} flex items-center justify-center mx-auto mb-1 md:mb-2.5`}>
                      <s.icon className={`h-4 w-4 md:h-5 md:w-5 ${s.color}`} />
                    </div>
                    <p className={`text-xl md:text-3xl font-bold ${s.textColor}`}>{britelabStats[s.key]}</p>
                    <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 font-medium">{s.label}</p>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
            {widgets.recentActivity && (
              <Card className="lg:col-span-2 border-0 shadow-sm overflow-hidden" data-testid="recent-activity">
                <div className="flex items-center justify-between px-3 md:px-5 pt-3 md:pt-5 pb-2 md:pb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-amber-500" />
                    <h3 className="text-xs md:text-sm font-bold text-slate-700">Recent Activity</h3>
                  </div>
                  <Link to="/forum"><Button variant="ghost" size="sm" className="text-xs text-brand-navy h-7">View all <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button></Link>
                </div>
                <div className="px-1.5 md:px-2 pb-2 md:pb-3 divide-y divide-slate-50">
                  {recentPosts.length > 0 ? recentPosts.map(post => (
                    <Link key={post.post_id} to={`/forum?post=${post.post_id}`} className="block"
                      data-testid={`recent-activity-${post.post_id}`}>
                      <div className="flex items-start gap-2 md:gap-3 p-2 md:p-3 rounded-lg hover:bg-slate-50 transition-colors group cursor-pointer">
                        <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg ${post.type === 'britelab' ? 'bg-purple-500' : 'bg-brand-navy'} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          {post.type === 'britelab' ? <FlaskConical className="h-3.5 w-3.5 md:h-4 md:w-4 text-white" /> : <MessageSquare className="h-3.5 w-3.5 md:h-4 md:w-4 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs md:text-sm font-medium text-slate-700 group-hover:text-brand-navy transition-colors truncate">{post.title}</p>
                          <p className="text-[10px] md:text-xs text-slate-400 mt-0.5 truncate">{post.author_name} · {post.comment_count ?? post.comments?.length ?? 0} comments</p>
                        </div>
                        <span className="text-[10px] text-slate-300 whitespace-nowrap mt-1">{timeAgo(post.created_at)}</span>
                      </div>
                    </Link>
                  )) : <p className="text-xs text-slate-400 text-center py-4">No recent activity</p>}
                </div>
              </Card>
            )}

            {widgets.upcomingEvents && (
              <Card className={`border-0 shadow-sm overflow-hidden ${!widgets.recentActivity ? 'lg:col-span-3' : ''}`} data-testid="upcoming-events-card">
                <div className="flex items-center justify-between px-3 md:px-5 pt-3 md:pt-5 pb-2 md:pb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-brand-sage" />
                    <h3 className="text-xs md:text-sm font-bold text-slate-700">Upcoming</h3>
                  </div>
                  <Link to="/events"><Button variant="ghost" size="sm" className="text-xs text-brand-navy h-7">All events <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button></Link>
                </div>
                <div className="px-3 md:px-5 pb-3 md:pb-4 space-y-2 md:space-y-3">
                  {upcomingEvents.length > 0 ? upcomingEvents.map(event => {
                    const d = new Date(event.date);
                    return (
                      <Link key={event.event_id} to="/events" className="block" data-testid={`dash-event-${event.event_id}`}>
                        <div className="flex items-start gap-2.5 md:gap-3 p-1.5 md:p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group">
                          <div className="w-10 md:w-11 flex-shrink-0 text-center">
                            <div className="text-[9px] md:text-[10px] font-bold uppercase text-brand-sage tracking-wide">{d.toLocaleString('en', { month: 'short' })}</div>
                            <div className="text-lg md:text-xl font-bold text-slate-800 leading-tight">{d.getDate()}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs md:text-sm font-medium text-slate-700 group-hover:text-brand-navy transition-colors truncate">{event.title}</p>
                            <p className="text-[10px] md:text-xs text-slate-400 mt-0.5 truncate">{event.location} · {event.format}</p>
                          </div>
                        </div>
                      </Link>
                    );
                  }) : <p className="text-xs text-slate-400 text-center py-4">No upcoming events</p>}
                </div>
              </Card>
            )}
          </div>

          {/* New Members & Partners */}
          {widgets.newMembersPartners && (newMembers.length > 0 || newPartners.length > 0) && (
            <div className="mb-4 md:mb-6 grid md:grid-cols-2 gap-3 md:gap-4" data-testid="new-members-partners-section">
              {/* New Members */}
              <Card className="border-0 shadow-sm overflow-hidden" data-testid="new-members-card">
                <div className="flex items-center justify-between px-3 md:px-5 pt-3 md:pt-5 pb-2 md:pb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-brand-sage" />
                    <h3 className="text-xs md:text-sm font-bold text-slate-700">New Members</h3>
                  </div>
                  <Link to="/members"><Button variant="ghost" size="sm" className="text-xs text-brand-navy h-7">All members <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button></Link>
                </div>
                <div className="px-2 md:px-3 pb-3 md:pb-4 space-y-1.5">
                  {newMembers.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No new members yet</p>
                  ) : newMembers.map(m => (
                    <Link key={m.user_id} to={`/profile/public/${m.user_id}`} className="block" data-testid={`new-member-${m.user_id}`}>
                      <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group">
                        <MemberAvatar
                          name={m.name}
                          picture={m.picture}
                          size={44}
                          ringClassName="ring-2 ring-brand-sage/20 group-hover:ring-brand-sage/40 transition-all"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs md:text-sm font-medium text-slate-700 group-hover:text-brand-navy transition-colors truncate">{m.name}</p>
                          <p className="text-[10px] md:text-xs text-slate-400 truncate">
                            {[m.profile?.role, m.company_name || m.profile?.organisation].filter(Boolean).join(' · ') || 'New community member'}
                          </p>
                        </div>
                        <span className="text-[10px] text-slate-300 whitespace-nowrap">{timeAgo(m.created_at)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </Card>

              {/* New Partners */}
              <Card className="border-0 shadow-sm overflow-hidden" data-testid="new-partners-card">
                <div className="flex items-center justify-between px-3 md:px-5 pt-3 md:pt-5 pb-2 md:pb-3">
                  <div className="flex items-center gap-2">
                    <Handshake className="h-4 w-4 text-brand-navy" />
                    <h3 className="text-xs md:text-sm font-bold text-slate-700">New Partners</h3>
                  </div>
                  <Link to="/partners"><Button variant="ghost" size="sm" className="text-xs text-brand-navy h-7">All partners <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button></Link>
                </div>
                <div className="px-2 md:px-3 pb-3 md:pb-4 space-y-1.5">
                  {newPartners.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No new partners yet</p>
                  ) : newPartners.map(p => {
                    const logoSrc = getPartnerLogoSrc(p);
                    return (
                    <Link key={p.sponsor_id} to={`/partners/${p.sponsor_id}`} className="block" data-testid={`new-partner-${p.sponsor_id}`}>
                      <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg overflow-hidden flex-shrink-0 bg-white border border-slate-200 flex items-center justify-center group-hover:border-brand-navy/30 transition-colors">
                          {logoSrc ? (
                            <img src={logoSrc} alt={p.name} className="w-full h-full object-contain p-1"
                              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex'; }} />
                          ) : null}
                          <div className="fallback-icon w-full h-full items-center justify-center" style={logoSrc ? { display: 'none' } : { display: 'flex' }}>
                            <Building2 className="h-5 w-5 text-brand-navy/30" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs md:text-sm font-medium text-slate-700 group-hover:text-brand-navy transition-colors truncate">{p.name}</p>
                          <p className="text-[10px] md:text-xs text-slate-400 truncate">
                            {p.sector || p.partner_type || 'Community partner'}
                          </p>
                        </div>
                        <span className="text-[10px] text-slate-300 whitespace-nowrap">{timeAgo(p.created_at)}</span>
                      </div>
                    </Link>
                    );
                  })}
                </div>
              </Card>
            </div>
          )}

          {/* Latest News */}
          {widgets.latestNews && latestNews.length > 0 && (
            <div className="mb-4 md:mb-6" data-testid="latest-news-section">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="flex items-center gap-2">
                  <Newspaper className="h-4 w-4 text-amber-600" />
                  <h3 className="text-xs md:text-sm font-bold text-slate-700">Latest News</h3>
                </div>
                <Link to="/news"><Button variant="ghost" size="sm" className="text-xs text-brand-navy h-7" data-testid="latest-news-view-all">
                  View all <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                </Button></Link>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {latestNews.map(item => {
                  const dateLabel = item.published_at || item.published;
                  const externalLink = item.external_url || item.link;
                  // RSS-only / external articles without a body in our DB
                  // open the source in a new tab. Anything with a news_id
                  // navigates to the dedicated /news/:newsId page (its own
                  // URL, shareable) rather than the old in-page modal.
                  const openInline = !!item.news_id;
                  const handleClick = (e) => {
                    e.preventDefault();
                    if (openInline) {
                      navigate(`/news/${item.news_id}`);
                      return;
                    }
                    if (externalLink) window.open(externalLink, '_blank', 'noopener,noreferrer');
                  };
                  return (
                    <button key={item.news_id || item.link} onClick={handleClick}
                      className="block group text-left w-full" data-testid={`latest-news-${item.news_id || ''}`}>
                      <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all h-full">
                        <div className="aspect-[16/10] bg-slate-100 overflow-hidden relative">
                          {item.image ? (
                            <img src={item.image} alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.news-fallback').style.display = 'flex'; }} />
                          ) : null}
                          <div className="news-fallback w-full h-full items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50"
                            style={item.image ? { display: 'none' } : { display: 'flex' }}>
                            <Newspaper className="h-8 w-8 text-amber-600/40" />
                          </div>
                          {item.source && (
                            <span className="absolute top-2 left-2 text-[9px] font-semibold uppercase tracking-wide bg-white/90 backdrop-blur px-1.5 py-0.5 rounded text-slate-700">
                              {item.source}
                            </span>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-xs md:text-sm font-semibold text-slate-800 group-hover:text-brand-navy transition-colors line-clamp-2 leading-snug">
                            {item.title}
                          </p>
                          {dateLabel && (
                            <p className="text-[10px] text-slate-400 mt-1.5">
                              {(() => {
                                try {
                                  return new Date(dateLabel).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                                } catch { return ''; }
                              })()}
                            </p>
                          )}
                        </div>
                      </Card>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recommended Companies */}
          {widgets.recommendations && recommendations.length > 0 && (
            <div className="mb-4 md:mb-6" data-testid="recommended-companies-section">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" />
                  <h3 className="text-xs md:text-sm font-bold text-slate-700">Recommended for You</h3>
                </div>
                <Link to="/companies"><Button variant="ghost" size="sm" className="text-xs text-brand-navy h-7">Browse all <ChevronRight className="h-3.5 w-3.5 ml-0.5" /></Button></Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
                {recommendations.map(rec => (
                  <Link key={rec.recommendation_id} to={`/companies/${rec.company_id}`} data-testid={`rec-company-${rec.recommendation_id}`}>
                    <Card className="p-3 md:p-4 border-0 shadow-sm bg-amber-50/40 hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer h-full">
                      <div className="flex items-center gap-2.5 md:gap-3">
                        <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-white border flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {rec.company?.logo_url ? <img src={rec.company.logo_url} alt="" className="w-full h-full object-contain p-1" /> : <Building2 className="h-4 w-4 text-slate-300" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-xs md:text-sm text-slate-800 truncate">{rec.company?.name || rec.company_name}</h4>
                          {rec.company?.sector && <p className="text-[10px] md:text-[11px] text-slate-400 truncate">{rec.company.sector}</p>}
                        </div>
                      </div>
                      {rec.note && <p className="text-[10px] md:text-[11px] text-slate-500 mt-1.5 md:mt-2 leading-relaxed line-clamp-2 italic">"{rec.note}"</p>}
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions — mobile-only (hidden on md+) */}
          {widgets.quickActions && (
            <div className="mb-4 md:hidden" data-testid="quick-actions-section">
              <h3 className="text-xs md:text-sm font-bold text-slate-700 mb-2 md:mb-3 flex items-center gap-2">
                <Target className="h-4 w-4 text-slate-400" /> Quick Actions
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3" data-testid="quick-actions-grid">
                {[
                  { to: '/forum', icon: MessageSquare, label: 'Forum', bg: 'bg-brand-navy' },
                  { to: '/members', icon: Users, label: 'Members', bg: 'bg-brand-sage' },
                  { to: '/events', icon: Calendar, label: 'Events', bg: 'bg-orange-600' },
                  { to: '/companies', icon: Building2, label: 'Companies', bg: 'bg-teal-600' },
                  { to: '/partners', icon: Handshake, label: 'Partners', bg: 'bg-cyan-600' },
                  { to: '/courses', icon: BookOpen, label: 'Courses', bg: 'bg-blue-600' },
                  { to: '/learning', icon: FileText, label: 'Learning', bg: 'bg-amber-600' },
                  { to: '/britelabs', icon: FlaskConical, label: 'BriteLabs', bg: 'bg-purple-600' },
                  { to: '/messages', icon: Send, label: 'Messages', bg: 'bg-emerald-600' },
                  { to: '/profile', icon: UserCircle, label: 'My Profile', bg: 'bg-slate-600' },
                  ...(user?.role === 'admin' ? [{ to: '/admin', icon: Shield, label: 'Admin', bg: 'bg-rose-600' }] : []),
                ].map(a => (
                  <Link key={a.to} to={a.to}>
                    <Card className="p-2.5 md:p-3 border-0 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer text-center group" data-testid={`quick-${a.label.toLowerCase()}`}>
                      <div className={`w-8 h-8 md:w-10 md:h-10 ${a.bg} rounded-xl flex items-center justify-center mx-auto mb-1 md:mb-2 group-hover:scale-110 transition-transform`}>
                        <a.icon className="h-4 w-4 md:h-5 md:w-5 text-white" />
                      </div>
                      <p className="text-[10px] md:text-xs font-medium text-slate-600">{a.label}</p>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
            </div>
            <PageSideRail placement="progress" below={<DashboardRailWidgets user={user} />} />
          </div>
        </div>
      </main>

    </div>
  );
}
